varying vec2 vUv;
uniform vec2 u_res;
uniform float u_time;
uniform float u_appear;

#define PI 3.14159265359
#define TWO_PI 6.28318530718

// Simplex noise helper functions
vec4 permute(vec4 x) { return mod(((x * 34.0) + 1.0) * x, 289.0); }
vec4 taylorInvSqrt(vec4 r) { return 1.79284291400159 - 0.85373472095314 * r; }

float snoise(vec3 v) {
  const vec2 C = vec2(1.0/6.0, 1.0/3.0);
  const vec4 D = vec4(0.0, 0.5, 1.0, 2.0);

  vec3 i = floor(v + dot(v, C.yyy));
  vec3 x0 = v - i + dot(i, C.xxx);
  vec3 g = step(x0.yzx, x0.xyz);
  vec3 l = 1.0 - g;
  vec3 i1 = min(g.xyz, l.zxy);
  vec3 i2 = max(g.xyz, l.zxy);

  vec3 x1 = x0 - i1 + C.xxx;
  vec3 x2 = x0 - i2 + 2.0 * C.xxx;
  vec3 x3 = x0 - 1.0 + 3.0 * C.xxx;

  i = mod(i, 289.0);
  vec4 p = permute(permute(permute(
    i.z + vec4(0.0, i1.z, i2.z, 1.0))
    + i.y + vec4(0.0, i1.y, i2.y, 1.0))
    + i.x + vec4(0.0, i1.x, i2.x, 1.0));

  float n_ = 1.0 / 7.0;
  vec3 ns = n_ * D.wyz - D.xzx;
  vec4 j = p - 49.0 * floor(p * ns.z * ns.z);
  vec4 x_ = floor(j * ns.z);
  vec4 y_ = floor(j - 7.0 * x_);
  vec4 x = x_ * ns.x + ns.yyyy;
  vec4 y = y_ * ns.x + ns.yyyy;
  vec4 h = 1.0 - abs(x) - abs(y);

  vec4 b0 = vec4(x.xy, y.xy);
  vec4 b1 = vec4(x.zw, y.zw);
  vec4 s0 = floor(b0) * 2.0 + 1.0;
  vec4 s1 = floor(b1) * 2.0 + 1.0;
  vec4 sh = -step(h, vec4(0.0));

  vec4 a0 = b0.xzyw + s0.xzyw * sh.xxyy;
  vec4 a1 = b1.xzyw + s1.xzyw * sh.zzww;

  vec3 p0 = vec3(a0.xy, h.x);
  vec3 p1 = vec3(a0.zw, h.y);
  vec3 p2 = vec3(a1.xy, h.z);
  vec3 p3 = vec3(a1.zw, h.w);

  vec4 norm = taylorInvSqrt(vec4(dot(p0,p0), dot(p1,p1), dot(p2,p2), dot(p3,p3)));
  p0 *= norm.x;
  p1 *= norm.y;
  p2 *= norm.z;
  p3 *= norm.w;

  vec4 m = max(0.6 - vec4(dot(x0,x0), dot(x1,x1), dot(x2,x2), dot(x3,x3)), 0.0);
  m = m * m;
  return 42.0 * dot(m * m, vec4(dot(p0,x0), dot(p1,x1), dot(p2,x2), dot(p3,x3)));
}

void main() {
  float aspect = u_res.x / u_res.y;
  vec2 ndc = (vUv - 0.5) * 2.0;
  vec2 deformedNdc = ndc;
  deformedNdc.x *= aspect;

  if (deformedNdc.y < 0.0) {
    float factor = 1.0 + abs(deformedNdc.y) * 5.5;
    deformedNdc /= factor;
  }

  vec2 centeredUv = vUv - 0.5;
  centeredUv.y += 0.1;
  centeredUv.x *= aspect;

  float radius = sqrt(length(centeredUv));
  float angle = atan(centeredUv.y, centeredUv.x);
  float normalizedAngle = fract((angle + PI * 0.5) / TWO_PI);
  float peak = 1.0 - abs(normalizedAngle - 0.5) * 2.0;
  vec2 radialUv = vec2(peak, radius);

  float t = u_time;
  vec2 n = vec2(100.0);
  vec2 N = vec2(0.0);
  vec2 p = radialUv * 10.0 + sin(t * 0.1);
  float S = 0.5;
  float angleStep = 1.0;
  float cosA = cos(angleStep), sinA = sin(angleStep);

  for (int j = 1; j <= 30; j++) {
    float tmpX = p.x * cosA - p.y * sinA;
    p.y = p.x * sinA + p.y * cosA;
    p.x = tmpX;

    tmpX = n.x * cosA - n.y * sinA;
    n.y = n.x * sinA + n.y * cosA;
    n.x = tmpX;

    vec2 q = p * S + float(j) + n + t;
    n += sin(q);
    N += cos(q) / S;
    S *= 1.2;
  }

  float mask = 1.0 - clamp(length(deformedNdc) - 0.2, 0.0, 1.0);
  float outMask = 1.0 - smoothstep(0.0, 1.0, length(deformedNdc) - 0.5);

  float noise = snoise(vec3(ndc, t * 0.2));
  noise = smoothstep(0.0, 0.5, (noise + 1.0) * 0.5) - smoothstep(0.5, 1.0, (noise + 1.0) * 0.5);
  noise = pow(noise, 10.0);

  vec3 baseColor = vec3(0.9137, 0.5922, 0.5686);
  vec3 noiseColor = baseColor * noise * (1.0 - mask) * outMask;

  float Nsum = dot(N, vec2(1.0));
  vec3 col = baseColor * pow(Nsum + 0.005 / length(N) + 0.2, 2.1);
  col = mix(baseColor, vec3(0.055), col.r);
  col = clamp(col, vec3(0.0), vec3(10.0));

  vec3 finalCol = col * (1.0 - mask) + noiseColor * 30.0;

  float globalMaskNoise = snoise(vec3(ndc * 10.0, t * 0.2));
  globalMaskNoise = clamp(smoothstep(1.0 - (u_appear * 1.5), 1.0, (globalMaskNoise + 1.0) * 0.5), 0.0, 1.0);

  gl_FragColor = vec4(finalCol, col.r * (1.0 - mask) * outMask * globalMaskNoise);
}
